// @file Surface permissions store
import { __ } from '@@/bits/intl'
import { reload } from '@@/bits/location'
import { getVuexStore } from '@@/bits/pinia'
import { getRoleChangeMessage } from '@@/bits/surface_permission_helpers'
import { isRegistered } from '@@/bits/user_model'
import { SnackbarNotificationType } from '@@/enums'
import { useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfaceStartingStateStore } from '@@/pinia/surface_starting_state'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { UserId, WallId } from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import { defineStore } from 'pinia'
import { computed } from 'vue'

const useSurfacePermissionsStore = defineStore('surfacePermissions', () => {
  const surfaceVuexStore = getVuexStore<RootState>() // For gradual conversion to pinia

  const surfaceStore = useSurfaceStore()
  const surfaceWishArrangementStore = useSurfaceWishArrangementStore()
  const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
  const surfaceStartingStateStore = useSurfaceStartingStateStore()

  const amIOwner = computed<boolean>(() => surfaceVuexStore?.getters.amIOwner)
  const amICollaborator = computed<boolean>(() => surfaceVuexStore?.getters.amICollaborator)
  const amIRegistered = computed<boolean>(() => isRegistered(surfaceVuexStore?.state.user))
  const canIAdminister = computed<boolean>(() => surfaceVuexStore?.getters.canIAdminister)
  const canIRemake = computed<boolean>(() => surfaceVuexStore?.getters.canIRemake)
  const canIEdit = computed<boolean>(() => surfaceVuexStore?.getters.canIEdit)
  const canIComment = computed<boolean>(() => surfaceVuexStore?.getters.canIComment)
  const canIReact = computed<boolean>(() => surfaceVuexStore?.getters.canIReact)
  const canIWrite = computed<boolean>(() => surfaceVuexStore?.getters.canIWrite)
  const canIModerate = computed<boolean>(() => surfaceVuexStore?.getters.canIModerate)
  const canIPost = computed<boolean>(() => surfaceVuexStore?.getters.canIPost)
  const canIPostWithoutWaitingForApproval = computed<boolean>(
    () => surfaceVuexStore?.getters.canIPostWithoutWaitingForApproval,
  )
  const canIDragSections = computed(
    () =>
      canIModerate.value &&
      (surfaceWishArrangementStore.isGroupBySection || surfaceWishArrangementStore.isGroupBySingleSelect) &&
      !surfaceStore.isFrozen &&
      !surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop,
  )
  const canIBookmarkWall = computed<boolean>(() => amIRegistered.value)
  const canIAccessContentDirectly = computed<boolean>(
    // Default to false for now to prevent accidental access to content
    () => surfaceStartingStateStore.startingState.canIAccessContentDirectly ?? false,
  )
  const requireApprovalForPosts = computed<boolean>(() => surfaceVuexStore?.getters.requireApproval)

  const canUpdateWallSettings = computed<boolean>(() => {
    return (
      !surfaceStore.isReadOnly &&
      !surfaceStore.isFrozen &&
      !surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop &&
      canIAdminister.value
    )
  })

  // Whether a user can create a new group for posts (eg section or single-select option)
  const canCreateNewGroup = computed(() => {
    return canIModerate.value && !surfaceStore.isFrozen && !surfaceStore.isReadOnly
  })

  function showPermissionToast(payload: { role: string; changed_group?: string }): void {
    const message = getRoleChangeMessage({
      ...payload,
      library_type: surfaceStore.isSchoolLibrary ? 'school' : surfaceStore.isClassroomLibrary ? 'classroom' : undefined,
    })
    if (message != null) {
      useGlobalSnackbarStore().setSnackbar({
        message,
        notificationType: SnackbarNotificationType.warning,
        persist: true,
        dismissable: true,
        actionText: __('Refresh'),
        actionTextActions: [() => reload()],
      })
    }
  }

  function handleCollaboratorChange({
    user_id: collaboratorId,
    wall_id: wallId,
    role,
  }: {
    user_id?: UserId
    wall_id?: WallId
    role?: string
  }): void {
    const isCurrentUser = collaboratorId != null && collaboratorId === surfaceVuexStore?.state.user.id
    const isCurrentWall = wallId != null && wallId === surfaceVuexStore?.state.wall.id

    if (!isCurrentUser && !isCurrentWall) return

    if (role == null) {
      reload()
      return
    }

    showPermissionToast({ role })
  }

  function handleAccessSettingsChange({ role, changed_group }: { role?: string; changed_group?: string } = {}): void {
    // Backend sends role: null when access is revoked — reload without showing a toast.
    if (role == null) {
      reload()
      return
    }

    // Whiteboard toolbar/frame UI depends on canI* store flags that aren't reactive to
    // privacy_policy changes, so a role change needs a reload to pick up the new permissions.
    if (surfaceStore.isWhiteboard) {
      reload()
      return
    }

    showPermissionToast({ role, changed_group })
  }

  // Named helpers for privacy_policy access level fields. These come from the
  // Privacy::Option constants on the backend (0 = NOT_ALLOWED, >0 = allowed at some level).
  function hasPublicAccess(publicAccess?: number): boolean {
    return publicAccess !== 0
  }

  function grantsAccess(accessLevel?: number): boolean {
    return (accessLevel ?? 0) > 0
  }

  // Safety net: detect "no access" from edit_wall privacy_policy when the
  // access_settings_updated event might not arrive (e.g. Brahms disconnects first).
  // All other role changes are handled by access_settings_updated which has the correct role.
  function handleEditWallPermissionChange(privacyPolicy: {
    public_access?: number
    tenant_access?: number
    library_access?: number
  }): void {
    if (hasPublicAccess(privacyPolicy.public_access)) return
    if (amIOwner.value || amICollaborator.value) return
    if (grantsAccess(privacyPolicy.tenant_access) || grantsAccess(privacyPolicy.library_access)) return

    reload()
  }

  return {
    // getters
    amIOwner,
    amICollaborator,
    amIRegistered,
    canIAdminister,
    canIModerate,
    canIRemake,
    canIPost,
    canIPostWithoutWaitingForApproval,
    canIComment,
    canIReact,
    canIWrite,
    canIEdit,
    canIDragSections,
    canIBookmarkWall,
    canIAccessContentDirectly,
    requireApprovalForPosts,
    canUpdateWallSettings,
    canCreateNewGroup,

    // Actions
    handleCollaboratorChange,
    handleAccessSettingsChange,
    handleEditWallPermissionChange,
  }
})
export { useSurfacePermissionsStore }
